import { persistentAtom } from '@nanostores/persistent';
import { atom, computed } from 'nanostores';
import { logger } from '@nanostores/logger';
import {
  notifyBundleChanges,
  clearNotifications
} from './bundle-notifications-store';

// Initiate an atom store for bundleItems which is synced to local storage
export const $bundleItems = persistentAtom('bundleItems', [], {
  encode: JSON.stringify,
  decode: JSON.parse
});

// Initiates a random bundleId for the bundle, the bundle Id is reset when the bundle is added to cart or cleared
export const $bundleId = persistentAtom(
  'bundleId',
  Math.random().toString(16).slice(2)
);

// Computed property that returns the total items in the bundle
export const $bundleItemsCount = computed($bundleItems, bundleItems => {
  return bundleItems.reduce((n, { quantity }) => n + quantity, 0);
});

/**
 * Computes the total boxes needed to ship the bundle
 * If the max box size is 12 and there are 18 items in the bundle,
 * Then 2 boxes will be required to ship the order
 *
 * @returns {Number}
 */
export const $boxCount = computed($bundleItemsCount, bundleItemsCount => {
  if (bundleItemsCount == 0) {
    return 1;
  }

  const maxBoxSize = Number(globalThis.PxlApp.maxBoxSize);

  return Math.ceil(bundleItemsCount / maxBoxSize);
});

// In memory atom store, which stores the transient bundle Items
// This is initiated to the value of bundleItems
export const $transientBundleItems = atom([...$bundleItems.get()]);

// Computed property that returns the total items in the transientBundle
export const $transientBundleItemsCount = computed(
  $transientBundleItems,
  bundleItems => {
    return bundleItems.reduce((n, { quantity }) => n + quantity, 0);
  }
);

// In memory atom store to track the state of the bundle
export const $editState = atom(false);

// In memory atom store to track the expanded/collapsed state of the bundle
export const $expanded = atom(false);

if (globalThis.PxlApp.isDev) {
  logger({
    bundleItems: $bundleItems,
    bundleId: $bundleId,
    bundleItemsCount: $bundleItemsCount,
    transientBundleItems: $transientBundleItems,
    transientBundleItemsCount: $transientBundleItemsCount,
    editState: $editState,
    expanded: $expanded
  });
}

/**
 * Toggle the expanded state of the bundle builder
 *
 * @returns {null}
 */
export function toggleExpandedState() {
  $expanded.set(!$expanded.get());
}

/**
 * Change the bundle to edit mode
 *
 * @returns {null}
 */
export function editBundle() {
  $editState.set(true);
}

/**
 * Clear all the items from the bundle
 *
 * @returns {null}
 */
export function clearBundle() {
  $editState.set(false);
  $bundleItems.set([]);
  $transientBundleItems.set([]);
  clearNotifications();
  resetBundleId();
}

export function resetBundleId() {
  $bundleId.set(Math.random().toString(16).slice(2));
}

/**
 * Updates the bundleItems in the local storage after the changes are applied by the user to the bundle.
 *
 * @returns {null}
 */
export function applyBundleChanges() {
  $editState.set(false);
  const oldCount = $bundleItemsCount.get();
  $bundleItems.set([...$transientBundleItems.get()]);

  const newCount = $bundleItemsCount.get();

  notifyBundleChanges(oldCount, newCount);
}

export function removeFromBundle(index) {
  let items = $transientBundleItems.get();
  items.splice(index, 1);
  $transientBundleItems.set([...items]);
}

export function changeBundleItemQuantity(index, quantity) {
  let items = $transientBundleItems.get();
  items[index].quantity = quantity;

  $transientBundleItems.set([...items]);
}

/**
 * Add an item to the bundle store
 * When the item already exists in the bundle, increase the quantity
 * Else add a new entry to the store
 *
 * @param {Object} bundleItem
 * @param {string} bundleItem.variantId
 * @param {number} bundleItem.quantity
 * @param {string} bundleItem.title
 * @param {string} bundleItem.imageUrl
 * @returns {null}
 */
export async function addToBundle(bundleItem) {
  try {
    const oldCount = $bundleItemsCount.get();
    const existingEntry = $bundleItems
      .get()
      .find(item => item.variantId === bundleItem.variantId);
    if (existingEntry) {
      $bundleItems.set(
        $bundleItems.get().map(item => {
          if (item.variantId !== bundleItem.variantId) return item;
          return {
            ...item,
            quantity: Number(item.quantity) + bundleItem.quantity
          };
        })
      );
    } else {
      $bundleItems.set([...$bundleItems.get(), bundleItem]);
    }

    const newCount = $bundleItemsCount.get();

    $transientBundleItems.set([...$bundleItems.get()]);
    notifyBundleChanges(oldCount, newCount, bundleItem);
    return true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return false;
  }
}

/**
 * Syncs transientBundleItems with bundleItems from localStorage
 * Only syncs when not in edit mode to avoid overwriting user edits
 * Reads directly from localStorage to ensure fresh data after BFCache restore
 *
 * @returns {null}
 */
export function syncTransientBundleItems() {
  // Only sync if not in edit mode
  if (!$editState.get()) {
    // Read directly from localStorage to get the persisted value
    // This ensures we have fresh data after BFCache restore, even if
    // @nanostores/persistent hasn't rehydrated the in-memory store yet
    let persistedValue;
    try {
      persistedValue = localStorage.getItem('bundleItems');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to read bundleItems from localStorage:', error);
      persistedValue = undefined;
    }

    let parsedValue = [];
    if (typeof persistedValue === 'string') {
      try {
        parsedValue = JSON.parse(persistedValue);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse bundleItems from localStorage:', error);
        parsedValue = [];
      }
    }

    const safeArray = Array.isArray(parsedValue) ? parsedValue : [];

    // Update both stores to ensure they're in sync with persisted state
    $bundleItems.set([...safeArray]);
    $transientBundleItems.set([...safeArray]);
  }
}

/**
 * Returns the bundle item from the bundle store
 * @param {string} variantId - The ID of the variant to lookup within the bundle
 * @returns {Object} - The bundle item
 */
export async function getBundleItem(variantId) {
  return $bundleItems.get().find(item => {
    if (Number(item.variantId) === Number(variantId)) {
      return item;
    }
  });
}
